import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CompanyWorker, Contractor, ContractorPerson, DocumentNumberingService, PersonsService, SalesDocument, SalesDocumentClient } from '@icc/b2b/shared';
import { ConfigService, core } from '@icc/shared';
import { ClientService } from './client.service';
import { SalesDocumentService } from './sales-document.service';
import { DocumentManagerService } from './document-manager.service';
import { DocumentPricesService } from './document-prices.service';
import { DocumentAuthor } from 'libs/b2b/shared/src/lib/models/document-author.model';
import { NgxPermissionsService } from 'ngx-permissions';

@Injectable({
    providedIn: 'root',
})
export class SalesDocumentPersonsService {
    contractors: Contractor[] = [];
    workers: CompanyWorker[] = [];

    constructor(
        private configService: ConfigService,
        private http: HttpClient,
        private salesDocumentService: SalesDocumentService,
        private documentManagerService: DocumentManagerService,
        private documentPricesService: DocumentPricesService,
        private personsService: PersonsService,
        private permissionsService: NgxPermissionsService,
    ) {
        if (this.configService.user) {
            this.load();
        }
    }

    async load() {
        if (this.permissionsService.hasPermission('access-trade')) {
            this.personsService.load();
            this.loadContractors();
            this.loadWorkers();
        }
    }

    public async loadContractors() {
        await this.personsService.loaded;
        this.contractors = this.personsService.getContractors();
    }

    public async loadWorkers() {
        await this.personsService.loaded;
        this.workers = this.personsService.getWorkers();
    }


    changeClient(document: SalesDocument, {
        id,
        resourceId,
        workerId,
        person,
        addresses,
    }: {
        id: number;
        resourceId: number;
        workerId: number;
        person: ContractorPerson;
        addresses;
    }) {
        const contractor = core.copy(this.contractors.find((c) => c.id === Number(id)));
        if (contractor) {
            contractor.person = person ?? contractor.person;
            const { key: documentNumber } = DocumentNumberingService.generate(
                this.configService.user.documentNumberingScheme[
                    DocumentNumberingService.getNumberingScheme(
                        document
                    )
                ],
                {
                    buyerId: id,
                    client: contractor,
                    sellerUserCode: this.personsService.getSeller(document)?.code ?? '',
                    marketCode: this.configService.user.market,
                    documentCustomCode: document?.documentCustomCode ?? '',
                    number:
                        document?.number != null
                            ? String(document?.number)
                            : '',
                    version: Number(document?.version),
                    userId: this.configService.user.user.id,
                    quotationNumber: document?.documentNumber
                        ? document?.documentNumber.join('')
                        : '',
                    quotationVersion: Number(document?.version || 0),
                    rootDocumentCreationDate: document?.rootCreated,
                    creationDate: document?.created,
                }
            );
            this.documentManagerService.saveDocument(
                document?.uuid,
                {
                    buyerContractorId: id,
                    buyerCompanyId: contractor?.company?.id,
                    buyerResourceId: resourceId || null,
                    buyerUserId: contractor?.person?.userId ?? null,
                    documentNumber,
                    client: ClientService.convertToDocumentClient(contractor, addresses, document.client),
                },
                true
            );
            document.buyerContractorId = id;
            document.buyerCompanyId = contractor?.company?.id;
            document.buyerResourceId = resourceId || null;
            document.buyerUserId = contractor?.person?.userId ?? null;
            if (this.configService.user.user.ActiveCompanyLicenseType === 'license') {
                this.documentPricesService.recalculatePosition(
                    document,
                    null,
                    false,
                    false,
                    true,
                );
            }
        }
    }

    setNewClient(document: SalesDocument, data: SalesDocumentClient) {
        const { key: documentNumber } = DocumentNumberingService.generate(
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(document)
            ],
            {
                buyerId: null,
                client: data,
                sellerUserCode: this.personsService.getSeller(document)?.code ?? '',
                marketCode: this.configService.user.market,
                documentCustomCode: document?.documentCustomCode ?? '',
                number:
                    document?.number != null ? String(document?.number) : null,
                version: Number(document?.version),
                userId: this.configService.user.user.id,
                quotationNumber: document?.documentNumber
                    ? document?.documentNumber.join('')
                    : '',
                quotationVersion: Number(document?.version || 0),
                rootDocumentCreationDate: document?.rootCreated,
                creationDate: document?.created,
            }
        );
        this.documentManagerService.saveDocument(
            document?.uuid,
            {
                buyerCompanyId: null,
                buyerContractorId: null,
                buyerResourceId: null,
                buyerUserId: null,
                documentNumber,
                client: {
                    isCompany: data.isCompany ?? false,
                    firstName: data.firstName || '',
                    lastName: data.lastName || '',
                    reference: data.reference || '',
                    email: data.email || '',
                    phoneNumber: data.phoneNumber || '',
                    companyName: data.companyName || '',
                    companyVatNr: data.companyVatNr || '',
                    companyVatNrEu: data.companyVatNrEu || '',
                    deliveryId: null,
                    delivery: data.delivery
                        ? {
                              countryId: data.delivery.countryId || 'PL',
                              regionId: data.delivery.regionId || null,
                              subregionId: data.delivery.subregionId || null,
                              addressLine1: data.delivery.addressLine1 || '',
                              addressLine2: data.delivery.addressLine2 || '',
                              postalCode: data.delivery.postalCode || '',
                              city: data.delivery.city || '',
                          }
                        : null,
                    invoiceFromDelivery: data.invoiceFromDelivery,
                    invoice: data.invoice
                        ? {
                              countryId: data.invoice.countryId || 'PL',
                              regionId: data.invoice.regionId || null,
                              subregionId: data.invoice.subregionId || null,
                              addressLine1: data.invoice.addressLine1 || '',
                              addressLine2: data.invoice.addressLine2 || '',
                              postalCode: data.invoice.postalCode || '',
                              city: data.invoice.city || '',
                          }
                        : null,
                    billingDeliveryId: null,
                    billingDelivery: data.billingDelivery
                        ? {
                              countryId: data.billingDelivery.countryId || 'PL',
                              regionId: data.billingDelivery.regionId || null,
                              subregionId: data.billingDelivery.subregionId || null,
                              addressLine1: data.billingDelivery.addressLine1 || '',
                              addressLine2: data.billingDelivery.addressLine2 || '',
                              postalCode: data.billingDelivery.postalCode || '',
                              city: data.billingDelivery.city || '',
                          }
                        : null,
                    otherContact: data.otherContact,
                },
            },
            true
        );
        document.buyerCompanyId = null;
        document.buyerContractorId = null;
        document.buyerResourceId = null;
        document.buyerUserId = null;
        if (this.configService.user.user.ActiveCompanyLicenseType === 'license') {
            this.documentPricesService.recalculatePosition(document, null, false, false, true);
        }
    }

    async changeSeller(salesDocument: SalesDocument, sellerWorker: CompanyWorker, resourceId: number, deliveryAddressId:number, deliveryAddress) {
        const { key: documentNumber } = DocumentNumberingService.generate(
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(salesDocument)
            ],
            {
                buyerId: salesDocument.buyerContractorId,
                client: salesDocument.client || {
                    reference: '',
                },
                sellerUserCode: sellerWorker?.code ?? '',
                marketCode: this.configService.user.market,
                documentCustomCode: salesDocument?.documentCustomCode ?? '',
                number: salesDocument.number != null ? String(salesDocument?.number) : '',
                version: Number(salesDocument?.version),
                userId: this.configService.user.user.id,
                quotationNumber: salesDocument?.documentNumber
                    ? salesDocument?.documentNumber.join('')
                    : '',
                quotationVersion: Number(salesDocument?.version || 0),
                rootDocumentCreationDate: salesDocument?.rootCreated,
                creationDate: salesDocument?.created,
            }
        );
        const seller = this.personsService.getWorkerByUserId(sellerWorker?.person?.user.id);
        seller.company = this.configService.user.company;
        seller.delivery = deliveryAddress;
        this.documentManagerService.saveDocument(
            salesDocument?.uuid,
            {
                sellerUserId: sellerWorker?.person?.user?.id,
                sellerResourceId: resourceId,
                seller: ClientService.convertSellerToDocumentClient(seller),
                documentNumber,
            },
            true
        );
        salesDocument.sellerUserId = sellerWorker?.person?.user?.id;
    }

    async changeBuyer(buyDocument: SalesDocument, buyerWorker: CompanyWorker, resourceId: number, deliveryAddressId:number, deliveryAddress) {
        if (!buyDocument) {
            return;
        }
        const contractor = this.configService.user.contractor ?? null;
        const buyer = this.personsService.getWorkerByUserId(buyerWorker?.person?.user.id);
        buyer.company = this.configService.user.company;
        buyer.delivery = deliveryAddress;
        if (contractor) {
            contractor.person = {
                userId: buyerWorker?.person?.user.id,
                firstName: buyerWorker?.person?.first_name,
                lastName: buyerWorker?.person?.last_name,
            };
            contractor.delivery = deliveryAddress;
            contractor.deliveryId = deliveryAddressId;
            const { key: buyDocumentNumber } = DocumentNumberingService.generate(
                this.configService.user.documentNumberingScheme[
                    DocumentNumberingService.getNumberingScheme(
                        buyDocument
                    )
                ],
                {
                    buyerId: buyDocument.buyerCompanyId,
                    client: contractor,
                    sellerUserCode: await this.documentManagerService.getSellerUserCode(buyDocument) ?? '',
                    marketCode: this.configService.user.market,
                    documentCustomCode: buyDocument?.documentCustomCode ?? '',
                    number: buyDocument?.number != null ? String(buyDocument?.number) : '',
                    version: Number(buyDocument?.version),
                    userId: this.configService.user.user.id,
                    quotationNumber: buyDocument?.documentNumber
                        ? buyDocument?.documentNumber.join('')
                        : '',
                    quotationVersion: Number(buyDocument?.version || 0),
                    rootDocumentCreationDate: buyDocument?.rootCreated,
                    creationDate: buyDocument?.created,
                }
            );
            this.documentManagerService.saveDocument(
                buyDocument?.uuid,
                {
                    buyerUserId: contractor?.person?.userId ?? null,
                    buyerResourceId: resourceId,
                    documentNumber: buyDocumentNumber,
                    client: ClientService.convertSellerToDocumentClient(buyer, contractor, buyDocument.client),
                },
                true
            );
        }
    }

    getAuthor(document: SalesDocument): DocumentAuthor {
        if (document.author?.referenceName) {
            return document.author;
        }
        const user = (
            this.workers.find(
                (worker) => document?.authorUserId === worker.person?.user?.id
            ) ??
            this.configService.user.accountManagers.find(
                (worker) =>
                    Number(document?.authorUserId) === Number(worker.person?.user?.id)
            ) ??
            this.getContractorWorkers(
                this.contractors.find((c) => c.id === document?.client?.id)
            ).find((worker) => document?.authorUserId === worker.person.userId)
        );
        if (user) {
            return {
                userId: user.person?.user?.id ?? user.person?.userId ?? 0,
                companyId: document.authorCompanyId ?? 0,
                referenceName: user.person?.reference_name ?? '',
                email: user?.person?.email ?? '',
                phoneNumber: user?.person?.phone_number ?? '',
            };
        }
    }

    getDriver(driverUserId: number) {
        return this.configService.user.drivers.find(
            (driver) => driverUserId === driver.id
        );
    }

    private getContractorWorkers(contractor: Contractor) {
        const workers = [];
        if (contractor?.company?.workers) {
            Object.values(contractor?.company?.workers).forEach((el) => {
                workers.push(...el);
            });
        }
        return workers.map((worker) => ({
            id: worker.id,
            person: {
                reference_name: worker.referenceName,
                userId: worker.userId,
                email: worker?.email,
                phone_number: worker?.phoneNumber,
            },
            code: null,
        }));
    }

}
